import { Tool, CategoryInfo } from '@/types/tool';

export const categories: CategoryInfo[] = [
  {
    id: 'productivity',
    name: 'Productivity',
    description: 'Tools to boost your workflow and get things done',
    icon: 'Zap',
    color: 'from-primary/20 to-primary/5',
  },
  {
    id: 'utility',
    name: 'Utility',
    description: 'Essential everyday utilities at your fingertips',
    icon: 'Wrench',
    color: 'from-accent/20 to-accent/5',
  },
  {
    id: 'finance',
    name: 'Finance',
    description: 'Calculate and manage your finances easily',
    icon: 'Calculator',
    color: 'from-secondary/40 to-secondary/10',
  },
  {
    id: 'entertainment',
    name: 'Entertainment',
    description: 'Fun tools to brighten your day',
    icon: 'Gamepad2',
    color: 'from-primary/15 to-accent/10',
  },
];

export const tools: Tool[] = [
  // Productivity
  {
    id: 'notes',
    name: 'Quick Notes',
    description: 'Create and save quick notes with formatting support',
    category: 'productivity',
    icon: 'FileText',
    path: '/tools/notes',
    guide: 'Type your notes in the editor. Your notes are automatically saved to your browser.',
  },
  {
    id: 'todo',
    name: 'Todo List',
    description: 'Manage your tasks with a simple todo list',
    category: 'productivity',
    icon: 'CheckSquare',
    path: '/tools/todo',
    guide: 'Add tasks, mark them complete, and organize your day efficiently.',
  },
  {
    id: 'pomodoro',
    name: 'Pomodoro Timer',
    description: 'Stay focused with the Pomodoro technique',
    category: 'productivity',
    icon: 'Timer',
    path: '/tools/pomodoro',
    guide: 'Work for 25 minutes, take a 5-minute break. Repeat 4 times, then take a longer break.',
  },
  // Utility
  {
    id: 'qr-generator',
    name: 'QR Code Generator',
    description: 'Generate QR codes for any text or URL',
    category: 'utility',
    icon: 'QrCode',
    path: '/tools/qr-generator',
    guide: 'Enter any text or URL to generate a scannable QR code instantly.',
  },
  {
    id: 'color-picker',
    name: 'Color Picker',
    description: 'Pick colors and get HEX, RGB, and HSL values',
    category: 'utility',
    icon: 'Palette',
    path: '/tools/color-picker',
    guide: 'Select a color using the picker or enter values directly. Copy the color code you need.',
  },
  {
    id: 'unit-converter',
    name: 'Unit Converter',
    description: 'Convert between different units of measurement',
    category: 'utility',
    icon: 'ArrowLeftRight',
    path: '/tools/unit-converter',
    guide: 'Select a category, enter a value, and see instant conversions to other units.',
  },
  {
    id: 'password-generator',
    name: 'Password Generator',
    description: 'Generate strong, secure passwords',
    category: 'utility',
    icon: 'Key',
    path: '/tools/password-generator',
    guide: 'Customize length and character types to generate a secure password.',
  },
  // Finance
  {
    id: 'tip-calculator',
    name: 'Tip Calculator',
    description: 'Calculate tips and split bills easily',
    category: 'finance',
    icon: 'Receipt',
    path: '/tools/tip-calculator',
    guide: 'Enter the bill amount, tip percentage, and number of people to split.',
  },
  {
    id: 'loan-calculator',
    name: 'Loan Calculator',
    description: 'Calculate monthly payments and interest',
    category: 'finance',
    icon: 'Landmark',
    path: '/tools/loan-calculator',
    guide: 'Enter loan amount, interest rate, and term to see your monthly payment.',
  },
  {
    id: 'currency-converter',
    name: 'Currency Converter',
    description: 'Convert between major world currencies',
    category: 'finance',
    icon: 'DollarSign',
    path: '/tools/currency-converter',
    guide: 'Select currencies and enter an amount to see the conversion.',
  },
  // Entertainment
  {
    id: 'random-quote',
    name: 'Random Quote',
    description: 'Get inspired with random quotes',
    category: 'entertainment',
    icon: 'Quote',
    path: '/tools/random-quote',
    guide: 'Click to generate a new inspirational quote. Share it with friends!',
  },
  {
    id: 'dice-roller',
    name: 'Dice Roller',
    description: 'Roll virtual dice for games',
    category: 'entertainment',
    icon: 'Dices',
    path: '/tools/dice-roller',
    guide: 'Select the number and type of dice, then roll for random results.',
  },
  {
    id: 'coin-flip',
    name: 'Coin Flip',
    description: 'Flip a virtual coin for quick decisions',
    category: 'entertainment',
    icon: 'Coins',
    path: '/tools/coin-flip',
    guide: 'Click to flip the coin. Perfect for making quick 50/50 decisions.',
  },
];

export const getToolsByCategory = (category: string) => {
  return tools.filter((tool) => tool.category === category);
};

export const getToolById = (id: string) => {
  return tools.find((tool) => tool.id === id);
};
